import { TObject } from 'utils/types';

import {
  IExchangeRate,
  ISettlementCalculation,
  ISettlementContract,
  ISettlementContractWorkbook,
  IWorkbookComment,
  TSettlementContractData,
} from './types';

export const normalizeSettlementContract = (
  data: TSettlementContractData,
): ISettlementContract => {
  return {
    contractName: (data.contractName as string) || '',
    contractStartDate: (data.contractStartDate as string) || '',
    id: (data.id as number) || 0,
    merchant: {
      enabled: !!data?.merchant?.enabled,
      id: (data?.merchant?.id as number) || null,
      name: (data?.merchant?.name as string) || '',
    },
    paymentDates: (data?.paymentDates as string[]) || [],
    paymentDelay: (data?.paymentDelay as number) || null,
    paymentFee: (data?.paymentFee as number) || null,
    paymentFixedFee: (data?.paymentFixedFee as number) || null,
    paymentFixedFeeCurrency: (data?.paymentFixedFeeCurrency as string) || null,
    paymentMinAmount: (data?.paymentMinAmount as number) || null,
    paymentMinAmountCurrency: (data?.paymentMinAmountCurrency as string) || null,
    rollingReserveAmount: (data?.rollingReserveAmount as number) || null,
    rollingReserveCurrency: (data?.rollingReserveCurrency as string) || null,
    rollingReserveDate: (data?.rollingReserveDate as string) || null,
    rollingReservePaymentDelay: data?.rollingReservePaymentDelay as number,
    rollingReserveType: (data?.rollingReserveType as string) || null,
    settlementCurrency: (data?.settlementCurrency as string) || '',
    settlementTimeZoneOffset: (data?.settlementTimeZoneOffset as string) || '',
    terminalLinkIds: (data?.terminalLinkIds as number[]) || [],
    terminalLinksCount: (data?.terminalLinksCount as number) || 0,
  };
};

export const normalizeSettlementContractWorkbook = (
  data?: TObject,
): ISettlementContractWorkbook => {
  return {
    calculationDate: (data?.calculationDate as string) || '',
    contractId: (data?.contractId as number) || 0,
    dailyNet: (data?.dailyNet as number) || 0,
    dayOfWeek: (data?.dayOfWeek as number) || 0,
    debtBalance: (data?.debtBalance as number) || 0,
    debtBalanceAdjustment: data?.debtBalanceAdjustment as number,
    deposits: (data?.deposits as number) || 0,
    depositsAdjustment: data?.depositsAdjustment as number,
    id: (data?.id as number) || 0,
    rollingReserve: (data?.rollingReserve as number) || 0,
    rollingReserveCup: (data?.rollingReserveCup as number) || 0,
    rollingReserveRelease: (data?.rollingReserveRelease as number) || null,
    stlBalance: (data?.stlBalance as number) || 0,
    stlBalanceAdjustment: data?.stlBalanceAdjustment as number,
    stlHold: (data?.stlHold as number) || 0,
    stlPayment: (data?.stlPayment as number) || 0,
    stlPaymentComment: (data?.stlPaymentComment as string) || '',
    stlWait: (data?.stlWait as number) || 0,
    withdrawals: (data?.withdrawals as number) || 0,
    withdrawalsAdjustment: data?.withdrawalsAdjustment as number,

    dep: (data?.dep as number) || 0,
    depFixedFee: (data?.depFixedFee as number) || 0,
    depMdrFee: (data?.depMdrFee as number) || 0,
    depMinFee: (data?.depMinFee as number) || 0,
    depRR: (data?.depRR as number) || 0,
    depTotalFee: (data?.depTotalFee as number) || 0,

    wd: (data?.wd as number) || 0,
    wdFixedFee: (data?.wdFixedFee as number) || 0,
    wdMdrFee: (data?.wdMdrFee as number) || 0,
    wdMinFee: (data?.wdMinFee as number) || 0,
    wdTotalFee: (data?.wdTotalFee as number) || 0,

    refunds: (data?.refunds as number) || 0,
    refundsAdjustment: data?.refundsAdjustment as number,
    rf: (data?.rf as number) || 0,
    rfFixedFee: (data?.rfFixedFee as number) || 0,
    rfMdrFee: (data?.rfMdrFee as number) || 0,
    rfMinFee: (data?.rfMinFee as number) || 0,
    rfTotalFee: (data?.rfTotalFee as number) || 0,

    cb: (data?.cb as number) || 0,
    cbFixedFee: (data?.cbFixedFee as number) || 0,
    cbMdrFee: (data?.cbMdrFee as number) || 0,
    cbMinFee: (data?.cbMinFee as number) || 0,
    cbTotalFee: (data?.cbTotalFee as number) || 0,
    chargebacks: (data?.chargebacks as number) || 0,
    chargebacksAdjustment: data?.chargebacksAdjustment as number,

    paymentFeeRateValue: (data?.paymentFeeRateValue as number) ?? null,
    paymentFixedFeeValue: (data?.paymentFixedFeeValue as number) ?? null,
    paymentRequestValue: (data?.paymentRequestValue as number) ?? null,
  };
};

export const normalizeWorkbookComment = (data?: TObject): IWorkbookComment => {
  return {
    adjustmentType: (data?.adjustmentType as string) || '',
    amount: (data?.amount as number) || 0,
    comment: (data?.comment as string) || '',
    created: (data?.created as string) || '',
    id: (data?.id as number) || 0,
    includeInHold: Boolean(data?.includeInHold),
    keycloakId: (data?.keycloakId as string) || '',
    login: (data?.login as string) || '',
  };
};

export const normalizeWorkbookComments = (data?: TObject[]): IWorkbookComment[] => {
  return data?.map(normalizeWorkbookComment).sort((a, b) => b.id - a.id) || [];
};

export const normalizeSettlementCalculation = (data: TObject): ISettlementCalculation => {
  return {
    lastCalculation: (data?.lastCalculation as string) || '',
    progress: (data?.progress as number) || 0,
    status: (data?.status as string) || '',
  };
};

export const normalizeSettlementContractExchangeRates = (
  data?: TObject,
): IExchangeRate => {
  return {
    baseCurrency: (data?.baseCurrency as string) || null,
    date: (data?.date as string) || null,
    quoteCurrency: (data?.quoteCurrency as string) || null,
    rate: (data?.rate as number) || null,
  };
};
